import type { Action, Agent, Hook } from '@lucidtech/las-sdk-browser';
import { useCallback } from 'react';

import {
  toActionId,
  toAgentId,
  toHookId,
  useDeleteAction,
  useDeleteHook,
  useListHooks,
  useUpdateAgent,
  useUpdateHook,
} from '@/hooks/api';
import { isEmpty } from '@/utils';

export type UseDeleteTriggerOnSuccess = (action: Action) => void;
export type UseDeleteTriggerOnError = ({ error }: { error: Error }) => void;
export type UseDeleteTriggerCallbackArgs = {
  agentId: string;
  actionId: string;
  onSuccess?: UseDeleteTriggerOnSuccess;
  onError?: UseDeleteTriggerOnError;
};
export type UseDeleteTriggerCallback = ({
  agentId,
  actionId,
  onSuccess,
  onError,
}: UseDeleteTriggerCallbackArgs) => Promise<Action>;

export type UseDeleteTriggerOpts = {
  onSuccess?: UseDeleteTriggerOnSuccess;
  onError?: UseDeleteTriggerOnError;
};

export const useDeleteTrigger = ({ onSuccess: onSuccessFnBase, onError: onErrorFnBase }: UseDeleteTriggerOpts = {}) => {
  const { data: { data: hooks } = {} } = useListHooks();

  const {
    mutateAsync: updateAgent,
    isPending: isPendingUpdateAgent,
    isSuccess: isSuccessUpdateAgent,
    error: errorUpdateAgent,
  } = useUpdateAgent();

  const {
    mutateAsync: deleteAction,
    isPending: isPendingDeleteAction,
    isSuccess: isSuccessDeleteAction,
    error: errorDeleteAction,
  } = useDeleteAction();

  const {
    mutateAsync: deleteHook,
    isPending: isPendingDeleteHook,
    isSuccess: isSuccessDeleteHook,
    error: errorDeleteHook,
  } = useDeleteHook();

  const {
    mutateAsync: updateHook,
    isPending: isPendingUpdateHook,
    isSuccess: isSuccessUpdateHook,
    error: errorUpdateHook,
  } = useUpdateHook();

  const deleteTrigger: UseDeleteTriggerCallback = useCallback(
    async ({ agentId, actionId, onSuccess: onSuccessFn, onError: onErrorFn }: UseDeleteTriggerCallbackArgs) => {
      const onSuccess: UseDeleteTriggerOnSuccess = (...args) => {
        onSuccessFnBase?.(...args);
        onSuccessFn?.(...args);
      };
      const onError: UseDeleteTriggerOnError = (...args) => {
        onErrorFnBase?.(...args);
        onErrorFn?.(...args);
      };

      try {
        const deletedHookIds: string[] = [];

        for (const hook of hooks ?? []) {
          const updates: Partial<Hook> = {};

          if (hook.trueActionId === actionId) {
            updates.trueActionId = null;
          }

          if (hook.falseActionId === actionId) {
            updates.falseActionId = null;
          }

          if (!isEmpty(updates)) {
            const afterUpdate = { ...hook, ...updates } as Hook;
            if ([afterUpdate.trueActionId, afterUpdate.falseActionId].every((id) => id == null)) {
              await deleteHook(toHookId(hook.hookId));
              deletedHookIds.push(hook.hookId);
            } else {
              await updateHook({ id: toHookId(hook.hookId), updates });
            }
          }
        }

        const action = await deleteAction(toActionId(actionId));

        await updateAgent({
          id: toAgentId(agentId),
          updates: (agent: Agent) => ({
            resourceIds: [...agent.resourceIds.filter((id) => id !== actionId && !deletedHookIds.includes(id))],
          }),
        });

        onSuccess?.(action);

        return action;
      } catch (error) {
        if (error instanceof Error) {
          onError?.({ error });
        } else {
          onError?.({ error: new Error('An unknown error occurred') });
        }
        throw error;
      }
    },
    [onSuccessFnBase, onErrorFnBase, deleteAction, updateAgent, hooks, deleteHook, updateHook]
  );

  return {
    deleteTrigger,
    isPending: isPendingDeleteHook || isPendingUpdateHook || isPendingDeleteAction || isPendingUpdateAgent,
    isSuccess: isSuccessDeleteHook || isSuccessUpdateHook || isSuccessDeleteAction || isSuccessUpdateAgent,
    errors: [errorDeleteHook, errorUpdateHook, errorDeleteAction, errorUpdateAgent].filter((e) => !!e),
  };
};
